/**
 * Renders children when the user has an account; otherwise shows a translated
 * "login required" message and opens the auth dialog. Keeps the user on the
 * current URL and shows page content after successful login.
 */

import { Outlet, useLocation } from '@tanstack/react-router';
import React, { useEffect, useRef } from 'react';

import { useAccount } from '~/apis';
import { useTranslation } from '~/app';
import {
  ROUTE_ACCOUNT,
  ROUTE_ACCOUNT_SUBSCRIPTIONS,
  ROUTE_FREEGAMES,
  ROUTE_FREEGAMES_CREATE,
  ROUTE_TOURNAMENTS,
} from '~/app/routes';
import { clearProtectedRouteReturn, setProtectedRouteReturn } from '~/auth/protectedRouteStorage';
import Container from '~/components/Container';
import { Button } from '~/components/Form/Button';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import LockIcon from '~/components/LockIcon';

import { useAuthDispatch, useAuthState } from './AuthContext';

/** i18n keys for protected route page titles (same as used by the actual page components). */
const PROTECTED_PATH_TITLE_KEYS: Record<string, string> = {
  [ROUTE_ACCOUNT]: 'profile_title',
  [ROUTE_ACCOUNT_SUBSCRIPTIONS]: 'subscription_title',
  '/table': 'button_table',
  '/toplist': 'toplist_title',
  [ROUTE_FREEGAMES]: 'setup_freeHeader',
  [ROUTE_FREEGAMES_CREATE]: 'setup_freeHeader',
  [ROUTE_TOURNAMENTS]: 'setup_tournamentHeader',
};

function getProtectedPageTitleKey(pathname: string): string {
  return PROTECTED_PATH_TITLE_KEYS[pathname] ?? 'protected_loginRequired';
}

export function ProtectedRouteGuard(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const pageTitleKey = getProtectedPageTitleKey(location.pathname);
  const pageTitle = t(pageTitleKey);
  const { data: account, isLoading: isAccountLoading } = useAccount();
  const { state: authState, isAuthSettled, isAuthorized } = useAuthState();
  const authDispatch = useAuthDispatch();
  const didRequestLogin = useRef(false);

  const isInitializing = authState === 'initializing';
  // Wait for auth to settle (startup refresh / cross-tab sync) before treating as unauthenticated.
  // Avoids false positives when multiple tabs reload and state is briefly unclear.
  const isLoading = isInitializing || !isAuthSettled || isAccountLoading;
  // Require both JWT + Firebase session and a loaded account. When useAccount is disabled,
  // React Query can still expose cached profile data briefly after logout; isAuthorized
  // must gate the protected view so the login gate appears immediately after sign-out.
  const isUnauthenticated = !isLoading && (!isAuthorized || !account);

  // Allow opening the login dialog again after a successful login.
  useEffect(() => {
    if (!isUnauthenticated) {
      didRequestLogin.current = false;
    }
  }, [isUnauthenticated]);

  // Store current path so we stay on this page after login (no redirect away).
  useEffect(() => {
    if (isUnauthenticated && location.pathname) {
      const returnPath = location.pathname + (location.searchStr ?? '') + (location.hash ?? '');
      setProtectedRouteReturn(returnPath);
    }
  }, [isUnauthenticated, location.pathname, location.searchStr, location.hash]);

  // Clear stored return path once we have an account (recovery complete).
  useEffect(() => {
    if (account) {
      clearProtectedRouteReturn();
    }
  }, [account]);

  // Open login dialog once when we know the user is not authenticated.
  useEffect(() => {
    if (!isUnauthenticated || didRequestLogin.current) return;
    didRequestLogin.current = true;
    authDispatch({ type: 'login' });
  }, [authDispatch, isUnauthenticated]);

  if (isLoading) {
    return (
      <Container
        header={pageTitle}
        headerIcon={<LockIcon className="h-5 w-5 shrink-0" color="#fff" />}
        className="relative max-w-3xl"
      >
        <div className="flex min-h-[200px] items-center justify-center">
          <LoadingSpinner size="md" />
        </div>
      </Container>
    );
  }

  if (isUnauthenticated) {
    return (
      <Container
        header={pageTitle}
        headerIcon={<LockIcon className="h-5 w-5 shrink-0" color="#fff" />}
        className="relative max-w-3xl"
      >
        <div className="flex overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
          <div className="flex shrink-0 items-center justify-center bg-gray-100 px-4 py-6 sm:px-6">
            <LockIcon className="h-12 w-12 sm:h-14 sm:w-14" color="#6b7280" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-3 p-5 sm:p-6">
            <h2 className="text-base leading-snug font-semibold text-gray-900">
              {t('protected_loginRequired')}
            </h2>
            <p className="text-sm leading-relaxed text-gray-700">
              {t('protected_loginRequiredDescription')}
            </p>
            <p className="text-xs leading-relaxed text-gray-500">
              {t('protected_loginRequiredHint')}
            </p>
            <div className="pt-1">
              <Button
                type="button"
                variant="primary"
                onClick={() => authDispatch({ type: 'login' })}
                className="w-fit"
              >
                {t('protected_openLoginRegister')}
              </Button>
            </div>
          </div>
        </div>
      </Container>
    );
  }

  return <Outlet />;
}
